import { formatPersonName } from '@/lib/format-display-name'

export type PersonIdentityInput = {
  name?: string | null
  greeting_name?: string | null
  email?: string | null
  id?: string | number | null
  avatar_url?: string | null
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
}

export type PersonDisplayMeta = {
  formalName: string
  displayName: string
  initials: string
  colorSeed: string
  useSilhouette: boolean
}

function resolvedFullName(input: PersonIdentityInput): string {
  const name = (input.name ?? '').trim()
  if (name) return formatPersonName(name)

  const full = (input.full_name ?? '').trim()
  if (full) return formatPersonName(full)

  const combined = [input.first_name, input.last_name]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
    .join(' ')
  if (combined) return formatPersonName(combined)

  return ''
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
  }
  if (parts.length === 1 && parts[0][0]) {
    return parts[0][0].toUpperCase()
  }
  return ''
}

/** Resolve display name, initials, and identicon seed for a person. */
export function getPersonDisplayMeta(input: PersonIdentityInput | null | undefined): PersonDisplayMeta {
  if (!input) {
    return {
      formalName: '',
      displayName: '',
      initials: '',
      colorSeed: '',
      useSilhouette: true,
    }
  }

  const formalName = resolvedFullName(input)
  const greeting = (input.greeting_name ?? '').trim()
  const email = (input.email ?? '').trim()
  const colorSeed = String(input.id ?? email ?? formalName ?? 'profile')

  if (formalName) {
    const initials = initialsFromName(formalName)
    return {
      formalName,
      displayName: greeting ? formatPersonName(greeting) : formalName,
      initials,
      colorSeed,
      useSilhouette: !initials,
    }
  }

  return {
    formalName: '',
    displayName: email,
    initials: '',
    colorSeed,
    useSilhouette: true,
  }
}

export type PersonIdentityDraft = {
  name: string
  greeting_name: string
}

/** Draft from account identity fields (settings). */
export function buildPersonIdentityDraftFromAccount(
  identity: { name?: string | null; greeting_name?: string | null } | null | undefined,
): PersonIdentityDraft {
  return {
    name: (identity?.name ?? '').trim(),
    greeting_name: (identity?.greeting_name ?? '').trim(),
  }
}

/** Draft for onboarding when account fields may be empty (profile/auth fallbacks). */
export function buildPersonIdentityDraftWithFallbacks(
  identity: { name?: string | null; greeting_name?: string | null } | null | undefined,
  profile: PersonIdentityInput | null | undefined,
  authName: string,
): PersonIdentityDraft {
  const fromAccount = (identity?.name ?? '').trim()
  const fromProfile = getPersonDisplayMeta(profile ?? undefined).formalName
  return {
    name: fromAccount || fromProfile || authName.trim(),
    greeting_name: (identity?.greeting_name ?? '').trim(),
  }
}
